import { createApi } from '@reduxjs/toolkit/query/react'
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { ReverseGeocodingResponseModel } from '../types/ReverseGeocodingResponseModel' 
import type { GeocodingParamsModel } from '../types/GeocodingParamsModel'
import type { LocationInfoModel } from '../types/LocationInfoModel'
import type { GeocodingErrorModel } from '../types/GeocodingErrorModel'
import { CoordinatesModel } from '../types/CoordinatesModel'

const GOOGLE_API_KEY = `AIzaSyDqAVbBDldBaW8hEGrvAfI044RX0TNOtFo`

export const geocodingApi = createApi({
    reducerPath: 'geocodingApi',
    baseQuery: fetchBaseQuery({
        baseUrl: 'https://maps.googleapis.com/maps/api/geocode/',
    }),
    tagTypes: ['Location'],
    endpoints: (builder) => ({
        reverseGeocoding: builder.query<LocationInfoModel, GeocodingParamsModel>({
            query: (params) => {
                const { latlng, language = 'en', result_type = [] } = params

                const queryParams: Record<string, string> = {
                    latlng,
                    key: GOOGLE_API_KEY!,
                    language,
                }

                if (result_type.length > 0) {
                    queryParams.result_type = result_type.join('|')
                }

                return {
                    url: 'json',
                    params: queryParams,
                }
            },
            transformResponse: (response: ReverseGeocodingResponseModel): LocationInfoModel => {
                if (response.status !== 'OK' || response.results.length === 0) {
                    throw new Error(response.error_message || 'No results found')
                }

                const result = response.results[0]
                const addressComponents = result.address_components

                const getComponent = (types: string[]): string | undefined => {
                    const component = addressComponents.find((comp) =>
                        types.some(type => comp.types.includes(type))
                    )

                    return component ? component.long_name : undefined
                }

                return {
                    address: result.formatted_address,
                    city: getComponent(['locality', 'political']),
                    country: getComponent(['country', 'political']),
                    region: getComponent(['administrative_area_level_1']),
                    postalCode: getComponent(['postal_code']),
                    street: getComponent(['route']),
                    houseNumber: getComponent(['street_number']),
                    fullInfo: result
                }
            },
            transformErrorResponse: (response: { status: number, data: any }): GeocodingErrorModel => {
                return {
                    status: response.data?.status || 'UNKNOWN_ERROR',
                    error_message: response.data?.error_message || 'Unknown error occurred',
                    code: response.status
                }
            },
            providesTags: ['Location'],
        }),

        batchReverseGeocoding: builder.query<LocationInfoModel[], { coordinates: CoordinatesModel[], language?: string }>({
            async queryFn({ coordinates, language = 'ru' }, _api, _extraOptions, fetchWithBQ) {
                try {
                    const results = await Promise.all(
                        coordinates.map(async (coord) => {
                            const result = await fetchWithBQ({
                                url: 'json',
                                params: {
                                    latlng: `${coord.lat},${coord.lng}`,
                                    key: GOOGLE_API_KEY!,
                                    language,
                                },
                            })

                            if (result.error) {
                                throw result.error
                            }

                            return result.data as ReverseGeocodingResponseModel
                        })
                    )

                    const locationInfos = results.map((result, index) => {
                        if (result.status !== 'OK' || result.results.length === 0) {
                            throw new Error(`No results for coordinates: ${coordinates[index].lat}, ${coordinates[index].lng}`)
                        }

                        const geocodingResult = result.results[0]
                        const addressComponents = geocodingResult.address_components

                        const getComponent = (types: string[]): string | undefined => {
                            const component = addressComponents.find((comp) =>
                                types.some(type => comp.types.includes(type))
                            )
                            return component ? component.long_name : undefined
                        }

                        return {
                            address: geocodingResult.formatted_address,
                            city: getComponent(['locality', 'political']),
                            country: getComponent(['country', 'political']),
                            postalCode: getComponent(['postal_code']),
                            street: getComponent(['route']),
                            houseNumber: getComponent(['street_number']),
                            fullInfo: geocodingResult
                        }
                    })

                    return { data: locationInfos }
                } catch (error) {
                    return { error: error as any }//TODO
                }
            },
            providesTags: ['Location'],
        }),
    }),
})

export const {
    useReverseGeocodingQuery,
    useLazyReverseGeocodingQuery,
    useBatchReverseGeocodingQuery,
} = geocodingApi